import AwardsSection from "@components/new-ui/AwardsSection";
import CaseStudyFeatureSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import ProblemSectionCard, { ProblemSection } from "@components/solution/problemSectionCard";
import React from "react";
import { Feature as FeatureProps, FeaturesSection } from "./whatsapp-broadcast";

const herosectionData: HerosectionProps = {
    chipTitle: "",
    title: "AI WhatsApp Chatbot — Smarter Conversations, More Conversions",
    description: "Deploy AI chatbots to handle FAQs, qualify leads, and share information — helping you save both time and money.",
    primaryButtonText: "Start Free Trial",
    imageUrl: "https://assets.gallabox.com/gb-home/chatbot/whatsapp-chat-hero.png",
    showBookACall: true,
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Replace repetitive queries with 24/7 bot power",
    subtitle2: "Build intelligent chatbots without writing a single line of code",
    imageUrl: "https://assets.gallabox.com/gb-home/chatbot/bot-power.png",
    featureInfoList: [
        "Design drag-and-drop workflows for order updates, booking confirmations, or returns — in minutes.",
        "Serve customers in 90+ languages, day or night, without hiring a global support team. ",
        "Free agents from routine chats (e.g., “Where’s my order?”) to tackle complex issues faster.",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Sell directly in WhatsApp chats",
    subtitle2: "Turn conversations into checkouts — no app-switching",
    imageUrl: "https://assets.gallabox.com/gb-home/chatbot/whatsapp-chat.png",
    featureInfoList: [
        "Showcase product catalogs inside chats (with prices, images, SKUs).",
        "Let customers pick sizes, add items to carts, and pay without leaving WhatsApp.",
        "Auto-send shipping updates and reorder reminders to boost repeat sales.",
    ],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Assign leads like a pro – no micromanaging",
    subtitle2: "Route chats to the right rep instantly ",
    imageUrl: "https://assets.gallabox.com/gb-home/chatbot/ai-power.png",
    featureInfoList: [
        "Set rules to assign chats based on criteria like location, department, or agent availability.",
        "Let bots qualify leads (e.g., “What’s your budget?”) before handing off warm prospects to sales.",
        "Balance workloads by automatically rotating assignments among team members.",
    ],
};

const featureInfo1: FeatureInfo = {
    mainTitle: "AI-powered conversations",
    subtitle2: "Go beyond “Hi [Name]” — make every reply feel human",
    imageUrl: "https://assets.gallabox.com/gb-home/chatbot/assign-leads.png",
    featureInfoList: [
        "Activate ChatGPT to turn robotic scripts into dynamic, context-aware conversations.",
        "Personalize conversations based on customer data and previous interactions. ",
        "Fix broken language translations instantly to avoid “Sorry, I don’t understand” moments.",
    ],
};

const featureInfo5: FeatureInfo = {
    mainTitle: "Guide customers smarter with WhatsApp Flows",
    subtitle2: "Create form-based journeys right inside chats ",
    imageUrl: "https://assets.gallabox.com/gb-home/chatbot/guide-customer.png",
    featureInfoList: [
        "Customers can book, register, and provide feedback without leaving WhatsApp",
        "Use rich inputs (date pickers, drop-downs) to collect accurate info the first time.",
        "Combine Flows with AI-powered chat to build seamless, guided experiences that keep conversations going and drop-offs low.",
    ],
};

export const ProblemSectionData: ProblemSection = {
    maintitle: "Benefits of using WhatsApp chatbot platform",
    problemSectionCard: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/filter.png",
            title: "Scan and qualify leads 24/7",
            description: "Your chatbot screens prospects (“Budget? Timeline?”), routes hot leads to sales, and nurtures curious buyers with tailored content — automatically.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/bot.svg",
            title: "Resolve routine tickets",
            description: "Bots handle tracking, returns, and FAQs with visual guides. Your team focuses on high-stakes fires like refunds or churn risks.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/profile-image.svg",
            title: "Respond and nurture effortlessly",
            description: "Auto-send rich-media guides (videos/PDFs) the moment prospects ask. No delays, no ghosting — just 90%+ reply rates.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/share.svg",
            title: "Share catalogs, demos, and guides",
            description: "Zero dead links — just 5X higher engagement by showcasing products via PDFs, send video tutorials, or embed pricing within chats. ",
        },
    ],
};

const sampleData: FeatureProps[] = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/chatbot/bot-using-ai.png",
        title: "How to build an AI Bot in Gallabox",
        url: "https://www.youtube.com/watch?v=g702Vdy_2wA&list=PLqLBaOO-5zr0PB-3iUlGVS2hZxxmzE5qN",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/chatbot/bot-template.png",
        title: "How to build a Bot using a template in Gallabox",
        url: "https://www.youtube.com/watch?v=tE3J2Yn3Wow&list=PLqLBaOO-5zr0PB-3iUlGVS2hZxxmzE5qN&index=3",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/chatbot/bot-from-scratch.png",
        title: "How to create a Bot from scratch in Gallabox",
        url: "https://www.youtube.com/watch?v=MhRs_iAfVEI&list=PLqLBaOO-5zr0PB-3iUlGVS2hZxxmzE5qN&index=4",
    },
];

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/rostrum-card-image.png",
        chiptitle: "EDUCATION",
        title: "Gallabox transforms WhatsApp marketing for Rostrum Education",
        statsInfoData: [
            { percentage: "60%", label: "Increase in customer satisfaction" },
            { percentage: "40%", label: "Boost in lead generation" },
            { percentage: "20%", label: "Growth in customer base" },
        ],
        url: "/case-study/rostrum-education",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/thread-bucket-studio/thread-bucket-studio.png",
        chiptitle: "E-COMMERCE",
        title: "Why Thread Bucket Studio chose Gallabox over Wati for seamless team communication",
        statsInfoData: [
            { percentage: "68%", label: "Higher business growth" },
            { percentage: "50%", label: "Rise in employee productivity" },
            { percentage: "30%", label: "Growth in customer base" },
        ],
        url: "/case-study/thread-bucket-studio",
    },

    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        chiptitle: "SERVICE",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        statsInfoData: [
            { percentage: "25%", label: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp" },
            { percentage: "47%", label: "Higher CSAT ratings" },
            { percentage: "31%", label: "Growth from WhatsApp" },
        ],
        url: "/case-study/dubai-housekeeping",
    },
];

const faqData: FAQSectionProps = {
    title: "FAQs about WhatsApp Chatbot Platform",
    faqItems: [
        {
            question: "How do I integrate a WhatsApp Chatbot with my existing WhatsApp Business account?",
            answer: "Sign up with a WhatsApp chatbot platform, get your WhatsApp Business account verified, and apply for WhatsApp Business API access. Once approved, use Gallabox’s drag-and-drop builder to design a custom WhatsApp chatbot in minutes based on your business needs, no coding required.",
        },
        {
            question: "Is coding required to set up a WhatsApp Chatbot on your platform?",
            answer: "You can build Gallabox’s WhatsApp Chatbot through 3 different methods: 1. With AI, 2. Using a template, or 3. From scratch. The best part is, you don’t need any coding knowledge or technical expertise. After building, you can add multiple flows to make a successful conversational chatbot.",
        },
        {
            question: "Can the WhatsApp Chatbot platform handle automated conversations at scale?",
            answer: "Yes, WhatsApp chatbot platforms can manage automated conversations at scale, offering quick, consistent responses without human intervention. Platforms like Gallabox, powered by the WhatsApp Business API, enable personalized, human-like interactions that feel natural, even at high volumes.",
        },
        {
            question: " How secure is the data collected through a WhatsApp Chatbot?",
            answer: "Since WhatsApp provides end-to-end encryption, the data collected through a WhatsApp Chatbot is always secure. This is another reason why using security-compliant chatbots like Gallabox’s WhatsApp Chatbot is important, as unofficial chatbots can result in data discrepancies or misuse.",
        },
        {
            question: "Can I integrate the WhatsApp Chatbot with other systems like CRMs or eCommerce platforms?",
            answer: "Yes, you can integrate WhatsApp Chatbot with your existing eCommerce platform or CRM. Gallabox allows seamless integration with CRMs such as Zoho, HubSpot, LeadSquared, Odoo, Kylas, CoreFactors, and LEDSAK, as well as eCommerce platforms like Shopify for streamlined operations.",
        },
        {
            question: "Does your WhatsApp Chatbot platform support multiple languages?",
            answer: "Yes, Gallabox’s Multilingual WhatsApp Chatbot is trained to optimize for popular languages. Instead of choosing basic translation tools, you can opt for this chatbot, as it provides precise, consistent, and context-aware responses. Instead of giving robotic translations, it gives natural responses.",
        },
        {
            question: "What kind of analytics and reporting does the WhatsApp Chatbot provide?",
            answer: "Through the WhatsApp Chatbot, you can get customer feedback. You can ask the customer to rate the conversation and provide additional comments, which in turn can help improve the bot’s responses and eventually engage the customer better.",
        },
        {
            question: "Is the WhatsApp Chatbot platform suitable for small businesses or only large enterprises?",
            answer: "The WhatsApp Chatbot platform is perfect for both small businesses and large enterprises. It automates customer interactions, handles FAQs, qualifies leads, and streamlines communication, improving efficiency and customer engagement for businesses of any size.",
        },
        {
            question: "What type of responses can the WhatsApp Chatbot deliver (e.g., text, images, videos)?",
            answer: "The WhatsApp Chatbot can deliver a variety of responses beyond text. It can send documents, images in JPEG or PNG formats, audio files in AAC, MP4, AMR, MPEG, and OGG formats, as well as video files in MP4 and 3GPP formats. This enables rich, multimedia communication with your customers.",
        },
        {
            question: "Does the WhatsApp Chatbot platform support multiple agents or teams?",
            answer: "Gallabox’s Shared Inbox feature can help you with this. With it, your full team can have access to all user-business conversations. The feature allows you to assign conversations depending on the needs and context to a bot or to agents on a round-robin basis / based on their availability.",
        },
    ],
};

const whatsappChatbot: React.FC = () => {
    return (
        <NewLayout
            title="AI-Powered WhatsApp Chatbot for Business — Gallabox (2026)"
            description="Build intelligent WhatsApp chatbots powered by AI. Automate conversations, qualify leads & resolve queries instantly. Trusted by 500+ businesses."
            canonical="https://gallabox.com/whatsapp-chatbot"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/chatbotx/whatsapp-chatbot-og.png" }}
            schema={{
                heroOrOgImageAlt: "whatsapp-chatbot-image",
                heroOrOgImageDescription: "Powerful WhatsApp Chatbot Platform to Automate & Grow Your Business",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/chatbot-rev/header.png",
                softwareFeatureName: "whatsapp-chatbot",
                faq: faqData.faqItems,
            }}
        >
            <Herosection herosection={herosectionData} />
            <section className={`${container_classname} my-10`}>
                <h3 className="text-center text-lg font-normal py-5">TRUSTED BY 10,000+ BUSINESSES</h3>
                <ClientLogos />
            </section>
            <h2 className="md:text-6xl text-4xl font-semibold pb-10 max-w-7xl mx-auto px-5" style={{ fontFamily: "Degular Display" }}>
                Key features and functionalities of WhatsApp Chatbot Platform
            </h2>
            <FeatureInfoCard {...featureInfo1} />
            <FeatureInfoCard {...featureInfo2} />
            <FeatureInfoCard {...featureInfo3} />
            <FeatureInfoCard {...featureInfo4} />
            <FeatureInfoCard {...featureInfo5} removeDivider />
            <ProblemSectionCard problemSection={ProblemSectionData} bgColor="var(--Grad-7, linear-gradient(265deg, #396AFF -7.73%, #00238D 61.31%, #000 102.16%))" customStyle />
            <FeaturesSection
                title="How to get started with WhatsApp chatbot platform"
                description="Create your WhatsApp chatbot effortlessly—let AI build it instantly, customize a ready template, or design fully from scratch. Automate conversations your way, with zero coding needed."
                features={sampleData}
            />
            <div className="mt-20">
                <CaseStudyFeatureSection features={caseStudyFeatureCardData} />
            </div>
            <FAQSection faqData={faqData} />
            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Integrate WhatsApp with your Facebook Ads today" primaryButtonText="Get free trial" />
        </NewLayout>
    );
};

export default whatsappChatbot;
